import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import PacienteSubviewLayout from "../components/paciente/PacienteSubviewLayout";
import HistoriaClinicaForm from "./HistoriaClinicaForm";
import PrestacionesPagos from "../components/PrestacionesPagos";
import Seguimiento from "../components/Seguimiento";
import Odontograma from "../components/Odontograma/Odontograma";
import RXPaciente from "../components/RXPaciente";
import RPPaciente from "../components/RPPaciente/RPPaciente";
import { API, authHeaders } from "../services/config";
import "./PacientesPage.css";

const VIEW_CONFIG = {
  historia: { title: "Historia Clínica" },
  prestaciones: { title: "Prestaciones y Pagos" },
  seguimiento: { title: "Seguimiento" },
  odontograma: { title: "Odontograma" },
  rx: { title: "Radiografías" },
  rp: { title: "Prescripciones" },
};

export default function PacienteSubview() {
  const { id, viewType } = useParams();
  const navigate = useNavigate();
  const config = VIEW_CONFIG[viewType];

  const [loading, setLoading] = useState(true);
  const [paciente, setPaciente] = useState(null);
  const [prestaciones, setPrestaciones] = useState([]);

  const fetchPaciente = useCallback(async () => {
    const response = await fetch(`${API}/pacientes/${id}`, { headers: authHeaders() });
    const json = await response.json();
    if (!response.ok || !json.success) throw new Error("No se pudo cargar el paciente");
    setPaciente(json.data);
  }, [id]);

  const fetchPrestaciones = useCallback(async () => {
    const response = await fetch(`${API}/pacientes/${id}/prestaciones`, { headers: authHeaders() });
    const json = await response.json();
    if (response.ok && json.success) setPrestaciones(json.data || []);
  }, [id]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchPaciente(), fetchPrestaciones()]);
    } catch (error) {
      console.error(error);
      navigate("/pacientes");
    } finally {
      setLoading(false);
    }
  }, [fetchPaciente, fetchPrestaciones, navigate]);

  useEffect(() => {
    if (!config) {
      navigate("/pacientes", { replace: true });
      return;
    }
    reload();
  }, [config, navigate, reload]);

  const totals = useMemo(() => {
    const totalCosto = prestaciones.reduce((acc, item) => acc + (parseFloat(item.costo) || 0), 0);
    const totalAbonado = prestaciones.reduce((acc, item) => acc + (parseFloat(item.abonado) || 0), 0);
    return { totalCosto, totalAbonado };
  }, [prestaciones]);

  const renderContent = () => {
    if (!paciente) return null;
    if (viewType === "historia") {
      return <HistoriaClinicaForm pacienteId={id} embedded onSaved={reload} />;
    }
    if (viewType === "prestaciones") {
      return <PrestacionesPagos embedded paciente={paciente} pacienteId={id} onSaved={reload} />;
    }
    if (viewType === "seguimiento") {
      return <Seguimiento embedded pacienteId={id} onSaved={reload} />;
    }
    if (viewType === "odontograma") {
      return <Odontograma embedded pacienteId={id} onSaved={reload} />;
    }
    if (viewType === "rx") {
      return (
        <RXPaciente
          embedded
          pacienteId={id}
          pacienteDni={paciente.dni}
          onSaved={reload}
        />
      );
    }
    if (viewType === "rp") {
      return (
        <RPPaciente
          embedded
          pacienteId={id}
          pacienteNombre={`${paciente.nombre || ""} ${paciente.apellido || ""}`.trim()}
          pacienteDni={paciente.dni}
          onSaved={reload}
        />
      );
    }
    return null;
  };

  return (
    <div className="pp-container">
      <Sidebar />
      <main className="pp-content">
        {loading ? (
          <p className="pd-cargando">Cargando paciente...</p>
        ) : (
          <PacienteSubviewLayout paciente={paciente} totals={totals} title={config?.title || "Paciente"}>
            {renderContent()}
          </PacienteSubviewLayout>
        )}
      </main>
    </div>
  );
}
